import { isTransporterDepartureDay } from '@carsfinders/contracts';

import { t } from '../../../../lib/i18n';
import { cn } from '../../utils';
import type { ScheduleHouseChip, ScheduleTimelineCell } from './session-schedule-types';
import { formatWeekdayShort } from './session-schedule-utils';

/**
 * Design D's fourteen-day matrix — admin port of
 * `apps/auction/.../components/AuctionScheduleTimelineReadOnly.tsx`.
 *
 * Cells carry no destination link — same reasoning as `SessionListView`: the
 * web original points at `/cars`, which the admin SPA does not have.
 */

interface SessionTimelineViewProps {
  /** Fourteen consecutive `YYYY-MM-DD` days, starting today. */
  days: string[];
  today: string;
  houses: ScheduleHouseChip[];
  /** Aggregated cells, keyed `${houseKey}|${date}`. */
  cells: Map<string, ScheduleTimelineCell>;
}

const COLUMNS = 'grid-cols-[180px_repeat(14,minmax(0,1fr))]';

export function SessionTimelineView({ days, today, houses, cells }: SessionTimelineViewProps) {
  const isWeekend = (date: string): boolean => {
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return weekday === 0 || weekday === 6;
  };

  const columnBackground = (date: string): string => {
    if (date === today) return 'bg-cf-accent/12';
    return isWeekend(date) ? 'bg-cf-surface-sunken' : 'bg-transparent';
  };

  return (
    <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-cf-line-soft px-5 py-3.5">
        <h2 className="text-[18px] font-semibold">{t('auctionSchedule.timeline.title')}</h2>
        <p className="text-[12px] text-cf-ink-subtle">{t('auctionSchedule.timeline.hint')}</p>
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="min-w-[900px]">
          <div className={cn('grid border-b border-cf-line-soft', COLUMNS)}>
            <div className="px-5 py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
              {t('auctionSchedule.timeline.house')}
            </div>
            {days.map((date) => (
              <div key={date} className={cn('px-1 py-2 text-center', columnBackground(date))}>
                <div className="font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase">
                  {formatWeekdayShort(date)}
                </div>
                <div
                  className={cn(
                    'font-mono text-[14px]',
                    date === today ? 'font-semibold text-cf-ink' : 'font-normal text-cf-ink-muted',
                  )}
                >
                  {Number(date.slice(8, 10))}
                </div>
              </div>
            ))}
          </div>

          {houses.map((chip) => (
            <div key={chip.house.key} className={cn('grid items-stretch border-b border-cf-line-soft', COLUMNS)}>
              <div className="flex items-center gap-2.5 px-5 py-3.5">
                <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-cf-xs" style={{ background: chip.color }} />
                <span className="min-w-0 text-[14px] leading-[1.2] font-semibold">{chip.house.name}</span>
              </div>

              {days.map((date) => {
                const cell = cells.get(`${chip.house.key}|${date}`);

                return (
                  <div
                    key={date}
                    className={cn(
                      'flex items-center justify-center border-l border-cf-line-faint px-1 py-2',
                      columnBackground(date),
                    )}
                  >
                    {cell && (
                      <span
                        title={`${chip.house.name} · ${cell.time} · ${cell.lots}`}
                        style={{ borderLeftColor: cell.dot }}
                        className={cn(
                          'flex h-11 w-full max-w-[56px] flex-col items-center justify-center rounded-cf-md border-l-[3px] leading-[1.1]',
                          cell.chip,
                        )}
                      >
                        <span className="font-mono text-[14px] font-medium">{cell.lots}</span>
                        <span className="font-mono text-[9px] opacity-80">{cell.time}</span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div className={cn('grid items-center', COLUMNS)}>
            <div className="flex items-center gap-2 px-5 py-3 text-[12px] text-cf-ink-subtle">
              <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-cf-ink" />
              {t('auctionSchedule.timeline.transporter')}
            </div>
            {days.map((date) => (
              <div key={date} className={cn('border-l border-cf-line-faint px-1 py-2 text-center', columnBackground(date))}>
                {isTransporterDepartureDay(date) && (
                  <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-cf-ink" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-[18px] border-t border-cf-line-soft px-5 py-3 text-[12px] leading-[1.5] text-cf-ink-muted">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[3px] bg-cf-accent" />
          {t('auctionSchedule.legend.soonShort')}
        </span>
        <span>{t('auctionSchedule.timeline.footnote')}</span>
      </div>
    </section>
  );
}
